import { DeleteOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, List, Modal, Row, Spin, Typography, Tooltip } from 'antd';
import { showErrorNotification } from '@med-fe/util';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserEmailAddress } from '../../store/user/user-reducer';
import {
  addRole,
  fetchPermissionsList,
  fetchRolePermissions,
  fetchRoles,
  fetchRoleUsers,
  mapRoleAdUser,
  mapRolePermission,
  unmapRoleAdUser,
  unmapRolePermission,
} from './actions';
import PermissionMappingsModal from './PermissionMappingsModal';
import {
  getPermissionsList,
  getPermissionsLoading,
  getRolePermissions,
  getRoles,
  getRolesLoading,
  getRoleUsers,
  getRoleUsersLoading,
  getSavingRole,
  getSavingUsers,
  getUserRoleConfigError,
  getUserRoleMappingError,
  getUserRoleMappingSaving,
} from './selectors';
import './UserRoleConfiguration.less';

const { Search } = Input;
const { Title } = Typography;

export default function UserRoleConfiguration() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [addUserForm] = Form.useForm();

  // store data/selectors
  const roles = useSelector(getRoles);
  const rolesLoading = useSelector(getRolesLoading);
  const permissionsList = useSelector(getPermissionsList);
  const permissionsLoading = useSelector(getPermissionsLoading);
  const roleUsersLoading = useSelector(getRoleUsersLoading);
  const savingUsers = useSelector(getSavingUsers);
  const savingRole = useSelector(getSavingRole);
  const error = useSelector(getUserRoleConfigError);
  const mappingSaving = useSelector(getUserRoleMappingSaving);
  const mappingError = useSelector(getUserRoleMappingError);

  // local UI state
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const selectedRole = useMemo(() => roles.find((r: any) => r.Id === selectedRoleId) ?? null, [roles, selectedRoleId]);

  // role-specific arrays (stable selector usage)
  const rolePermissionIds = useSelector((state: any) => getRolePermissions(state, selectedRoleId ?? 0));
  const roleUsers = useSelector((state: any) => getRoleUsers(state, selectedRoleId ?? 0));
  const currentUserEmail = useSelector(getUserEmailAddress);

  const [rolesSearch, setRolesSearch] = useState('');
  const [permSearch, setPermSearch] = useState('');
  const [usersSearch, setUsersSearch] = useState('');

  // local editable sets (initialized from selectors when role changes)
  const [checkedPermIds, setCheckedPermIds] = useState<Set<number>>(new Set());
  const [initialCheckedPermIds, setInitialCheckedPermIds] = useState<Set<number>>(new Set());

  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);

  // modal state
  const [isModalVisible, setIsModalVisible] = useState(false);

  // permission details modal (shows roles + users associated with a permission)
  const [permDetailsVisible, setPermDetailsVisible] = useState(false);
  const [viewingPermission, setViewingPermission] = useState<any>(null);

  // load roles + permissions list once
  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchPermissionsList());
  }, [dispatch]);

  // load role-specific data when selectedRoleId changes
  useEffect(() => {
    if (selectedRoleId !== null) {
      dispatch(fetchRolePermissions(selectedRoleId));
      // always fetch users mapped to the selected role
      dispatch(fetchRoleUsers(selectedRoleId));
    } else {
      setCheckedPermIds(new Set());
      setInitialCheckedPermIds(new Set());
    }
  }, [dispatch, selectedRoleId]);

  // initialize local checked sets when store rolePermissionIds / roleUserIds update
  useEffect(() => {
    const permSet = new Set<number>((rolePermissionIds || []).map((p: any) => Number(p)));
    if (selectedRole?.IsAdminRole) {
      const all = new Set<number>((permissionsList || []).map((p: any) => Number(p.Id)));
      setCheckedPermIds(all);
      setInitialCheckedPermIds(all);
    } else {
      setCheckedPermIds(permSet);
      setInitialCheckedPermIds(new Set(permSet));
    }
    // keep dependency stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rolePermissionIds || []), selectedRole?.IsAdminRole, JSON.stringify(permissionsList || [])]);

  // role users are provided by `roleUsers` selector; no local checkbox state needed

  // show errors
  useEffect(() => {
    if (error) {
      const msg = error?.errors?.[0] || error?.messages?.[0] || error?.message || 'Operation failed';
      showErrorNotification(msg);
    }
  }, [error]);

  // watch mapping (map/unmap) save status to show result messages and close add-user modal
  const prevMappingSavingRef = useMemo(() => ({ current: false }), [] as any);
  useEffect(() => {
    const prev = prevMappingSavingRef.current;
    if (prev && !mappingSaving) {
      // just finished — close add user modal on completion (notifications shown by sagas)
      setIsAddUserModalVisible(false);
      addUserForm.resetFields();
      if (selectedRoleId !== null) dispatch(fetchRoleUsers(selectedRoleId));
    }
    prevMappingSavingRef.current = mappingSaving;
  }, [mappingSaving, mappingError, addUserForm, dispatch, selectedRoleId, prevMappingSavingRef]);

  // filtered lists
  const filteredRoles = useMemo(
    () =>
      (roles || []).filter((r: any) =>
        `${r.DisplayName} ${r.Name} ${r.Id}`.toLowerCase().includes((rolesSearch || '').toLowerCase())
      ),
    [roles, rolesSearch]
  );

  const filteredPermissions = useMemo(
    () =>
      (permissionsList || [])
        .filter((p: any) => `${p.DisplayName || p.Name}`.toLowerCase().includes((permSearch || '').toLowerCase()))
        .sort((a: any, b: any) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0)),
    [permissionsList, permSearch]
  );

  const filteredUsers = useMemo(
    () =>
      (roleUsers || []).filter((u: any) =>
        `${u.Name || ''} ${u.Email || ''}`.toLowerCase().includes((usersSearch || '').toLowerCase())
      ),
    [roleUsers, usersSearch]
  );

  // toggle permission: optimistic update + immediate map/unmap api call
  const togglePerm = (id: number) => {
    if (!selectedRole) {
      showErrorNotification('Please select User Role');
      return;
    }
    if (selectedRole.IsAdminRole) return;

    setCheckedPermIds((prev) => {
      const next = new Set(prev);
      const had = next.has(id);
      if (had) {
        next.delete(id);
        dispatch(unmapRolePermission(selectedRole.Id, id));
      } else {
        next.add(id);
        dispatch(mapRolePermission(selectedRole.Id, id));

        // If the permission being added is a WRITE permission, also auto-add the corresponding READ permission
        try {
          const perm = (permissionsList || []).find((p: any) => Number(p.Id) === Number(id));
          const name = (perm?.DisplayName || perm?.Name || '').toString();
          if (/\bwrite\b/i.test(name)) {
            const targetName = name.replace(/\bwrite\b/i, 'Read');
            const readPerm = (permissionsList || []).find(
              (p: any) => (p.DisplayName || p.Name || '').toString().toLowerCase() === targetName.toLowerCase()
            );
            if (readPerm && !next.has(Number(readPerm.Id))) {
              next.add(Number(readPerm.Id));
              dispatch(mapRolePermission(selectedRole.Id, Number(readPerm.Id)));
            }
          }
        } catch (e) {
          // ignore
        }
      }
      return next;
    });
  };

  // delete (unmap) a user from role - pass email to backend
  // perform the actual delete/unmap (called after confirmation)
  const handleDeleteUser = (email: string) => {
    if (!selectedRole) return;
    (dispatch(unmapRoleAdUser(selectedRole.Id, email)) as any)?.then?.(() => {
      if (selectedRoleId !== null) dispatch(fetchRoleUsers(selectedRoleId));
    });
  };

  // validate then show a confirmation modal before deleting
  const askDeleteUser = (email: string, displayName?: string) => {
    if (!selectedRole) return;

    // Prevent removing last admin user: count unique, non-empty emails
    if (selectedRole.IsAdminRole) {
      const emails = (roleUsers || [])
        .map((u: any) => (u.Email || u.email || '').toString().toLowerCase().trim())
        .filter(Boolean);
      const unique = Array.from(new Set(emails));
      if (unique.length <= 1) {
        showErrorNotification('At least one user must remain assigned to the Admin role');
        return;
      }
      // allow current user to unmap themselves per updated requirement
    }

    Modal.confirm({
      title: 'Remove user from role',
      content: `Are you sure you want to remove ${displayName || email} from role ${selectedRole.DisplayName}?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => handleDeleteUser(email),
    });
  };

  // add(map) user by email
  const handleAddUser = (values: any) => {
    if (!selectedRole) return;
    const emailRaw = values.email || '';
    const email = emailRaw.toString().toLowerCase().trim();
    const alreadyMapped = (roleUsers || []).some(
      (u: any) => ((u.Email || u.email || '') as string).toString().toLowerCase().trim() === email
    );
    if (alreadyMapped) {
      showErrorNotification('User is already assigned to this role');
      return;
    }

    (dispatch(mapRoleAdUser(selectedRole.Id, emailRaw)) as any)?.then?.(() => {
      if (selectedRoleId !== null) dispatch(fetchRoleUsers(selectedRoleId));
      setIsAddUserModalVisible(false);
      addUserForm.resetFields();
    });
  };

  const handleSubmit = (values: any) => {
    const nameRaw = (values.name || '').toString().trim();
    const displayNameRaw = (values.displayName || '').toString().trim();

    const exists = (roles || []).some((r: any) => {
      const rName = (r.Name || '').toString().toLowerCase().trim();
      const rDisplay = (r.DisplayName || '').toString().toLowerCase().trim();
      return rName === nameRaw.toLowerCase() || rDisplay === displayNameRaw.toLowerCase();
    });
    if (exists) {
      showErrorNotification('Role with the same AD Group Name or Display Name already exists');
      return;
    }

    const body = {
      Name: values.name,
      DisplayName: values.displayName,
      DisplayOrder: null,
      IsActive: true,
      IsAdminRole: false,
      CreatedBy: '',
    };
    dispatch(addRole(body));
    setIsModalVisible(false);
    form.resetFields();
  };

  const showPermissionDetails = (permissionId: number, displayName?: string) => {
    setViewingPermission({ Id: permissionId, DisplayName: displayName });
    setPermDetailsVisible(true);
  };

  const closePermissionDetails = () => {
    setPermDetailsVisible(false);
    setViewingPermission(null);
  };

  return (
    <div className='urc-container' style={{ padding: 16 }}>
      <Title level={4}>User Role Configuration</Title>
      <Row gutter={16}>
        <Col span={6}>
          <div className='list-box'>
            <div
              className='list-header'
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}
            >
              <Title level={5} style={{ margin: 0 }}>
                User Roles
              </Title>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                size='small'
                onClick={() => setIsModalVisible(true)}
                style={{ marginLeft: 'auto' }} // Pushes the button to the right end
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Search
                placeholder='Search user roles'
                onChange={(e) => setRolesSearch(e.target.value)}
                value={rolesSearch}
              />
            </div>

            <Spin spinning={rolesLoading}>
              <List
                size='small'
                bordered
                dataSource={filteredRoles}
                renderItem={(role: any) => (
                  <List.Item
                    key={role.Id}
                    style={{
                      cursor: 'pointer',
                      background: selectedRole?.Id === role.Id ? '#e6f7ff' : undefined,
                    }}
                    onClick={() => setSelectedRoleId(role.Id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{role.DisplayName}</div>
                      </div>
                      {role.IsAdminRole ? <div style={{ color: '#d48806' }}>Admin</div> : null}
                    </div>
                  </List.Item>
                )}
                locale={{ emptyText: 'No roles' }}
                style={{ maxHeight: 560, overflow: 'auto' }}
              />
            </Spin>
          </div>
        </Col>

        <Col span={10}>
          <div className='list-box'>
            <div className='list-header' style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>
                Permissions
              </Title>
            </div>

            <div style={{ marginBottom: 8 }}>
              <Search
                placeholder='Search permissions'
                onChange={(e) => setPermSearch(e.target.value)}
                value={permSearch}
              />
            </div>

            <Spin spinning={permissionsLoading}>
              <List
                size='small'
                bordered
                dataSource={filteredPermissions}
                renderItem={(p: any) => {
                  const checked = checkedPermIds.has(Number(p.Id));
                  const disabled = selectedRole?.IsAdminRole === true;
                  return (
                    <List.Item
                      key={p.Id}
                      actions={[
                        <Tooltip title='Show mapped roles & users' key='info'>
                          <Button
                            type='text'
                            icon={<TeamOutlined />}
                            onClick={() => showPermissionDetails(Number(p.Id), p.DisplayName)}
                          />
                        </Tooltip>,
                      ]}
                    >
                      <Checkbox checked={checked} disabled={disabled} onChange={() => togglePerm(Number(p.Id))}>
                        <div style={{ display: 'inline-block', minWidth: 340 }}>
                          <div style={{ fontWeight: 500 }}>{p.DisplayName}</div>
                        </div>
                      </Checkbox>
                    </List.Item>
                  );
                }}
                locale={{ emptyText: 'No permissions' }}
                style={{ maxHeight: 560, overflow: 'auto' }}
              />
            </Spin>
            {/* Removed Select Visible / Clear / Save Permissions controls per requirement */}
          </div>
        </Col>

        <Col span={7}>
          <div className='list-box'>
            <div
              className='list-header'
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}
            >
              <Title level={5} style={{ margin: 0 }}>
                Users
              </Title>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                size='small'
                onClick={() => setIsAddUserModalVisible(true)}
                disabled={!selectedRole}
                style={{ marginLeft: 'auto' }} // Pushes the button to the right end
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <Search placeholder='Search users' onChange={(e) => setUsersSearch(e.target.value)} value={usersSearch} />
            </div>

            <Spin spinning={roleUsersLoading || savingUsers}>
              <List
                size='small'
                bordered
                dataSource={filteredUsers}
                renderItem={(u: any) => (
                  <List.Item
                    key={u.Id}
                    actions={[
                      <Button
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => askDeleteUser(String(u.Email), u.Name)}
                      />,
                    ]}
                  >
                    <div style={{ display: 'inline-block' }}>
                      <div style={{ fontWeight: 500 }}>{u.Name}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{u.Email}</div>
                    </div>
                  </List.Item>
                )}
                locale={{ emptyText: selectedRole ? 'No users' : 'Select a role to load users' }}
                style={{ maxHeight: 560, overflow: 'auto' }}
              />
            </Spin>
          </div>
        </Col>
      </Row>
      <Modal
        title='Add User to Role'
        visible={isAddUserModalVisible}
        onCancel={() => {
          setIsAddUserModalVisible(false);
          addUserForm.resetFields();
        }}
        footer={null}
      >
        <Form layout='vertical' form={addUserForm} onFinish={handleAddUser}>
          <Form.Item
            label='Email'
            name='email'
            rules={[
              { required: true, message: 'Please enter user email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder='User email' />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                onClick={() => {
                  setIsAddUserModalVisible(false);
                  addUserForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type='primary' loading={savingUsers} htmlType='submit'>
                Add
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title='Add New Role'
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form layout='vertical' form={form} onFinish={handleSubmit}>
          <Form.Item
            label='AD Group Name'
            name='name'
            rules={[{ required: true, message: 'Please enter AD Role Name' }]}
          >
            <Input placeholder='AD Group Name' />
          </Form.Item>
          <Form.Item
            label='Display Name'
            name='displayName'
            rules={[{ required: true, message: 'Please enter Display Name' }]}
          >
            <Input placeholder='Display Name' />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type='primary' loading={savingRole} htmlType='submit'>
                Submit
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      <PermissionMappingsModal
        visible={permDetailsVisible}
        permissionId={viewingPermission?.Id ?? null}
        displayName={viewingPermission?.DisplayName}
        onClose={closePermissionDetails}
      />
    </div>
  );
}
